export interface SubscriptionLike {
  provider: string;
  status: string;
  endDate: string | null;
  cancelAtPeriodEnd?: boolean | null;
}

/** What a subscription row means for the user today. Same rule on the users list and the details page. */
export type PlanState = "free" | "trial" | "active" | "ending" | "overdue" | "past_due" | "inactive";

const LIVE_STATUSES = new Set(["active", "trialing"]);
const FREE_PROVIDER = "free";

/** State of a live (active / trialing) row. */
const livePlanState = (sub: SubscriptionLike, endsAt: number | null, now: number): PlanState => {
  if (sub.provider === FREE_PROVIDER) return "free";
  // A trial runs until its first charge; cancelled during the trial it still runs to the end.
  if (sub.status === "trialing") return sub.cancelAtPeriodEnd ? "ending" : "trial";
  if (endsAt === null) return "active";
  if (endsAt <= now) return "overdue";
  return sub.cancelAtPeriodEnd ? "ending" : "active";
};

export const planState = (sub: SubscriptionLike, now: number = Date.now()): PlanState => {
  const endsAt = sub.endDate ? new Date(sub.endDate).getTime() : null;
  if (LIVE_STATUSES.has(sub.status)) return livePlanState(sub, endsAt, now);
  if (sub.status === "past_due") return "past_due";
  // Canceled mid-period keeps access until the paid period ends.
  if (sub.status === "canceled" && endsAt !== null && endsAt > now) return "ending";
  return "inactive";
};

export const hasAccess = (state: PlanState) => state !== "inactive";

/** The row that describes the user's plan right now: a live one first, else the newest. */
export const currentSubscription = <T extends SubscriptionLike>(subs: T[], now: number = Date.now()): T | null =>
  subs.find((s) => hasAccess(planState(s, now)) && planState(s, now) !== "free") ??
  subs.find((s) => hasAccess(planState(s, now))) ??
  subs[0] ??
  null;

export const PLAN_STATE_LABEL: Record<PlanState, string> = {
  free: "Free plan",
  trial: "Trial",
  active: "Active",
  ending: "Ends at period end",
  overdue: "Renewal overdue",
  past_due: "Payment past due",
  inactive: "Inactive",
};

export const PLAN_STATE_CLASS: Record<PlanState, string> = {
  free: "bg-sky-50 text-sky-700",
  trial: "bg-violet-50 text-violet-700",
  active: "bg-emerald-50 text-emerald-700",
  ending: "bg-amber-50 text-amber-700",
  overdue: "bg-amber-50 text-amber-700",
  past_due: "bg-red-50 text-red-700",
  inactive: "bg-gray-100 text-gray-600",
};
